/**
 * The in-app notification centre (KAN-82).
 *
 * The one channel that cannot be turned off, and the durable record every other
 * channel is a copy of. That is why the bell is in the shell rather than on a
 * page: an alert whose email bounced is still an alert the recipient has to be
 * able to find, and a notification centre you have to navigate to is one nobody
 * checks.
 *
 * ## Unread is per person
 *
 * Read state lives on the *delivery* row rather than on the alert, so an alert
 * sent to three people is read by one and still unread for the other two. A
 * flag on the alert would let the first reader clear everybody's badge, which
 * is the fastest way to make a notification centre untrusted.
 *
 * ## Why it polls
 *
 * A minute's polling rather than a websocket. The platform has no realtime
 * transport, adding one for a badge would be a connection to hold open, reap
 * and reconnect through every deploy — and the alerts themselves are evaluated
 * hourly. A badge that is up to sixty seconds behind an hourly pass is not a
 * meaningfully worse badge; it is a considerably simpler one.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AlertCode, AlertSeverity, ThresholdUnit } from '@accounting/shared'
import { apiFetch } from '../lib/api'
import { formatMoney, formatRelative } from '../lib/format'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'

interface NotificationRow {
  id: string
  alertId: string
  code: AlertCode
  label: string
  unit: ThresholdUnit
  severity: AlertSeverity
  status: string
  entityLabel: string | null
  trippedValue: number
  thresholdAtTrigger: number
  link: string
  triggeredAt: string
  readAt: string | null
}

const SEVERITY_TONE: Record<AlertSeverity, Tone> = {
  critical: 'critical',
  warning: 'warning',
  info: 'neutral',
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const panel = useRef<HTMLDivElement>(null)

  const count = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => apiFetch<{ unread: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  })

  const feed = useQuery({
    queryKey: ['notifications', 'feed'],
    queryFn: () => apiFetch<{ notifications: NotificationRow[] }>('/notifications'),
    /** Only fetched once the panel is opened — most sessions never open it. */
    enabled: open,
  })

  const markRead = useMutation({
    mutationFn: (ids?: string[]) =>
      apiFetch('/notifications/read', {
        method: 'POST',
        body: JSON.stringify(ids ? { ids } : {}),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  /**
   * Closes on an outside click or on Escape.
   *
   * Both, because they are different gestures by different people: a pointer
   * user dismisses by clicking away and a keyboard user has no "away" to click.
   * Omitting the second leaves somebody trapped in a panel they opened.
   */
  useEffect(() => {
    if (!open) return

    const onPointer = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const unread = count.data?.unread ?? 0

  return (
    <div className="relative" ref={panel}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        className="relative rounded-md p-2 text-ink-600 hover:bg-ink-100 hover:text-brand-800"
      >
        <BellIcon className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
            {/* Capped, because a three-digit badge is wider than the bell. */}
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-lg border border-ink-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
            <h2 className="font-display text-sm font-semibold text-brand-900">Notifications</h2>
            {unread > 0 && (
              <Button size="sm" onClick={() => markRead.mutate(undefined)}>
                Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {feed.isLoading && <p className="px-4 py-6 text-sm text-ink-600">Loading…</p>}

            {feed.data?.notifications.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-ink-600">
                Nothing yet. Alerts appear here whether or not the email reaches you.
              </p>
            )}

            {feed.data?.notifications.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => {
                  markRead.mutate([row.id])
                  setOpen(false)
                  navigate(row.link)
                }}
                className={`block w-full border-b border-ink-100 px-4 py-3 text-left last:border-b-0 hover:bg-ink-50 ${
                  row.readAt ? '' : 'bg-brand-50/40'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <Badge tone={SEVERITY_TONE[row.severity]}>{row.severity}</Badge>
                    <span className="text-sm font-medium text-brand-900">{row.label}</span>
                  </span>
                  <span className="shrink-0 text-xs text-ink-500">
                    {formatRelative(row.triggeredAt)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-ink-700">{row.entityLabel ?? 'The firm'}</p>
                <p className="mt-0.5 text-xs text-ink-500">
                  {formatValue(row.unit, row.trippedValue)} against a threshold of{' '}
                  {formatValue(row.unit, row.thresholdAtTrigger)}
                  {row.status === 'resolved' && ' · since resolved'}
                </p>
              </button>
            ))}
          </div>

          <div className="border-t border-ink-100 px-4 py-2 text-center">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                navigate('/account#notifications')
              }}
              className="text-xs text-brand-700 hover:underline"
            >
              Notification settings
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function formatValue(unit: ThresholdUnit, value: number): string {
  switch (unit) {
    case 'money':
      return formatMoney(value)
    case 'days':
      return `${value} day${value === 1 ? '' : 's'}`
    case 'bps':
      return `${(value / 100).toFixed(1)}%`
    case 'count':
      return String(value)
  }
}

/** Local to the bell: nothing else in the app needs it. */
function BellIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  )
}
